// src/pages/Manufacturing/components/ManufacturingTable.js
import React, { useEffect, useState } from "react";
import { Table, InputNumber, Button, Input, Row, Col, Card, Form } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  addRawRow,
  updateRawRow,
  deleteRawRow,
  applyRecipeRow,
  addProductionRow,
  updateProductionRow,
  deleteProductionRow,
  setFooterValues,
  calculateTotals,
} from "../manufacturingSlice";
import { fetchItems } from "../../../../store/commonThunks/lookups";
import InputNumberField from "../../../../components/Input/InputNumber";
import SearchListAutoComplete from "../../../../components/SearchListAutoComplete";
import { getCurrentStockApi } from "../../../../apiservice/sales";
import { bomGetByItemApi } from "../../../../apiservice/bom";
import Swal from "sweetalert2";
import "./manufacturingStyle.css"
import { selectAuthInfo } from "../../../Auth/authSelectors";
import { getCurrencySymbol } from "../../../../lib/currency";
import ProductQuickDrawer from "../../../Product_manage/components/drawers/ProductQuickDrawer";
import Big from "big.js";

const bigMul = (a, b) => Number(Big(a || 0).times(b || 0).toFixed(13));
const bigDiv = (a, b) => (b ? Number(Big(a || 0).div(b).toFixed(13)) : 0);
const bigAdd = (a, b) => Number(Big(a || 0).plus(b || 0).toFixed(13));

// Helper to calculate rate with tax
// const calculateRateWithTax = (rate, tax_per) => {
//   const baseRate = parseFloat(rate || 0);
//   const taxPercent = parseFloat(tax_per || 0);
//   return +(baseRate + (baseRate * taxPercent) / 100).toFixed(2);
// };

const ManufacturingTable = ({ type, mfId }) => {
  const dispatch = useDispatch();
  const { rawCart, productionCart, footerValues } = useSelector(
    (s) => s.manufacturing
  );
  const { items } = useSelector((s) => s.lookups); // ✅ items from lookupThunk

  const dataSource = type === "raw" ? rawCart : productionCart;
  const [productDrawer, setProductDrawer] = useState({ open: false, rowKey: null, });

  const authInfo = useSelector(selectAuthInfo);
  const currency = authInfo?.currency;
  const symbol = getCurrencySymbol(
    currency
  );
  const isPerfumeIndustry = authInfo?.branch_company_type === "Perfume Industry";
  // Cache of item_id -> { bom, items } | null (null = confirmed no active recipe).
  const [bomCache, setBomCache] = useState({});


  useEffect(() => {
    dispatch(fetchItems()); // fetch item list
  }, [dispatch]);

  const handleItemSelect = (rowKey, selectedItem) => {
    // const selectedItem = items.find((i) => i.item_id === itemId);


    if (!selectedItem) return;

    if (selectedItem) {

      const updates = {
        item_id: selectedItem.item_id,
        product: selectedItem.item_name,
        uom: selectedItem.uom || selectedItem?.unit_symbol,
        rate: 0,
        amount: 0,
        tax_per: selectedItem?.tax_per ?? 0,
        item_code: selectedItem?.item_code ?? "",
        item_percentage: type === "raw" ? 0 : productionCart.length === 0 ? 100 : 0,
        unit_id: selectedItem?.unit_id || 0,
        unit_name: selectedItem?.unit_name || "",
        unit_symbol: selectedItem?.unit_symbol || "",
        per_unit_id: selectedItem?.unit_id || 0,
        per_unit_symbol: selectedItem?.unit_symbol || "",
        per_conversion: selectedItem?.conversion || 1,
        warehouse_id: selectedItem?.warehouse_id || 0,
        warehouse_name: selectedItem?.warehouse_name || "",
        base_unit_name: selectedItem?.base_unit_name || "",
        conversion: selectedItem?.conversion || 1,
        units: selectedItem?.units || [],
        qty: null
      };

      let unitObj = [{ unit_symbol: updates?.unit_symbol, conversion: updates?.per_conversion, unit_id: updates?.unit_id }];

      updates.units = unitObj;
      if (type == "raw") {
        updates.rate = selectedItem.purchase_rate || selectedItem.sale_rate || 0;
        dispatch(updateRawRow({ rowKey, updates }));
      } else {
        dispatch(updateProductionRow({ rowKey, updates }));

        // Perfume Industry: look up whether this finished item has an active
        // recipe, so the "Apply Recipe" action can be offered on this row.
        if (isPerfumeIndustry && !(selectedItem.item_id in bomCache)) {
          bomGetByItemApi(selectedItem.item_id)
            .then((res) => {
              setBomCache((prev) => ({
                ...prev,
                [selectedItem.item_id]: res.data?.bom ? res.data : null,
              }));
            })
            .catch(() => {
              setBomCache((prev) => ({ ...prev, [selectedItem.item_id]: null }));
            });
        }
      }


      // Call getCurrentStockApi
      const warehouseId = selectedItem.warehouse_id || 0;
      getCurrentStockApi({ itemId: selectedItem.item_id, warehouseId })
        .then((res) => {
          const avgRate = parseFloat(res.data[0]?.average_rate || 0);
          const isManualRate = avgRate == 0;
          const conversion = parseFloat(res.data[0]?.conversion || 1);
          const rateWithConversion = bigMul(avgRate, conversion);
          const currentQty = parseFloat(res.data[0]?.current_qty || 0);
          console.log(rateWithConversion, "rateWithConversion");
          console.log("purchase_rate", selectedItem);
          const rateUpdate =
            type === "raw"
              ? { rate: rateWithConversion, raw_item_rate: rateWithConversion, current_stock: currentQty, manual_rate: isManualRate }
              : { rate: rateWithConversion, pd_rate: rateWithConversion, current_stock: currentQty };

          if (type === "raw") dispatch(updateRawRow({ rowKey, updates: rateUpdate }));
          else dispatch(updateProductionRow({ rowKey, updates: rateUpdate }));

          dispatch(calculateTotals());
        })
        .catch((err) => {
          if (err.response?.data?.message) {
            Swal.fire({ title: err.response.data.message, icon: "warning" });
          } else {
            Swal.fire({
              title: "Internal Error",
              text: "If the issue persists, please contact our support team.",
              icon: "warning",
            });
          }
        });
    }
  };

  useEffect(() => {
    if (mfId) return; // on edit mode no re calculation

    if (productionCart.length > 0 && productionCart[0].product !== '') {
      const totalAmount = rawCart.reduce((sum, r) => bigAdd(sum, r.amount || 0), 0);
      const totalProdQty = productionCart.reduce((sum, r) => bigAdd(sum, r.qty || 0), 0);
      const ratePerUnit = bigDiv(totalAmount, totalProdQty);

      // ✅ Only update if values actually differ (prevents infinite loop)
      productionCart.forEach((prodRow) => {
        const newRate = ratePerUnit;
        const newAmount = bigMul(prodRow.qty, ratePerUnit);

        if (prodRow.rate !== newRate || prodRow.amount !== newAmount) {
          dispatch(updateProductionRow({
            rowKey: prodRow.key,
            updates: { rate: newRate, amount: newAmount },
          }));
        }
      });
    }
  }, [
    dispatch,
    rawCart,
    // Track just qtys of productionCart
    // productionCart.map(r => r.qty).join(','),
    productionCart,
    mfId
  ]);


  const handleQtyChange = (rowKey, qty) => {
    const cart = type === "raw" ? rawCart : productionCart;
    const row = cart.find((r) => r.key === rowKey);
    if (!row) return;

    if (type === "raw" && row.current_stock != null) {
      const isMinusSale = authInfo.is_minus_stock_sale;
      if (qty > row.current_stock && isMinusSale === "no") {
        Swal.fire({
          icon: "warning",
          title: "Insufficient Stock",
          text: `Only ${row.current_stock} ${row.unit_symbol || row.uom || ""} available for ${row.product}`,
        });
        return;
      }
    }

    // 🟢 EDIT MODE
    if (mfId) {
      if (type === "raw") {
        const updates = {
          qty,
          amount: bigMul(qty, row.rate || 0),
        };
        dispatch(updateRawRow({ rowKey, updates }));

        if (productionCart.length > 0 && productionCart[0].product !== '') {
          // const newTotalRawAmount = rawCart.reduce(
          //   (sum, r) => sum + (r.key === rowKey ? qty * (row.rate || 0) : r.amount || 0),
          //   0
          // );
          const newTotalRawAmount = rawCart.reduce((sum, r) => {
            const value = r.key === rowKey ? bigMul(qty, row.rate || 0) : r.amount || 0;
            return bigAdd(sum, value);
          }, 0);
          const totalProdQty = productionCart.reduce((sum, r) => bigAdd(sum, r.qty || 0), Big(0));
          const ratePerUnit = bigDiv(newTotalRawAmount, totalProdQty);

          productionCart.forEach((prodRow) => {
            dispatch(updateProductionRow({
              rowKey: prodRow.key,
              updates: {
                rate: ratePerUnit,
                amount: bigMul(prodRow.qty, ratePerUnit),
              },
            }));
          });
        }
      } else {
        // Production qty change in edit mode
        const totalRawAmount = rawCart.reduce((sum, r) => bigAdd(sum, r.amount || 0), Big(0));
        const totalProdQty = productionCart.reduce(
          (sum, r) => sum + (r.key === rowKey ? qty : r.qty || 0),
          0
        );
        const ratePerUnit = totalProdQty ? totalRawAmount / totalProdQty : 0;

        productionCart.forEach((prodRow) => {
          const newQty = prodRow.key === rowKey ? qty : prodRow.qty || 0;
          dispatch(updateProductionRow({
            rowKey: prodRow.key,
            updates: {
              qty: newQty,
              rate: ratePerUnit,
              amount: +(newQty * ratePerUnit).toFixed(2),
            },
          }));
        });
      }

      dispatch(calculateTotals());
      return;
    }

    // 🔵 CREATE MODE
    if (type === "raw") {
      const updates = {
        qty,
        amount: +(qty * (row.rate || 0)).toFixed(2),
      };

      dispatch(updateRawRow({ rowKey, updates })); // ✅ dispatch first

      if (productionCart.length > 0 && productionCart[0].product !== '') {
        const newTotalRawAmount = rawCart.reduce(
          (sum, r) => sum + (r.key === rowKey ? qty * (row.rate || 0) : r.amount || 0),
          0
        );
        const totalProdQty = productionCart.reduce((sum, r) => sum + (r.qty || 0), 0);
        const ratePerUnit = totalProdQty ? newTotalRawAmount / totalProdQty : 0;

        productionCart.forEach((prodRow) => {
          dispatch(updateProductionRow({
            rowKey: prodRow.key,
            updates: {
              rate: ratePerUnit,
              amount: bigMul(prodRow.qty, ratePerUnit),
            },
          }));
        });
      }
    } else {
      // Production qty change in create mode
      const totalAmount = rawCart.reduce((sum, r) => sum + (r.amount || 0), 0);
      const totalProdQty = productionCart.reduce(
        (sum, r) => sum + (r.key === rowKey ? qty : r.qty || 0),
        0
      );
      const ratePerUnit = totalProdQty ? totalAmount / totalProdQty : 0;

      productionCart.forEach((prodRow) => {
        const newQty = prodRow.key === rowKey ? qty : prodRow.qty || 0;
        dispatch(updateProductionRow({
          rowKey: prodRow.key,
          updates: {
            qty: newQty,
            rate: ratePerUnit,
            amount: +(newQty * ratePerUnit).toFixed(2),
          },
        }));
      });
    }

    dispatch(calculateTotals());
  };

  const handleApplyRecipe = async (record) => {
    const bomEntry = bomCache[record.item_id];
    if (!bomEntry || !bomEntry.bom) return;

    const productionQty = Number(record.qty) || 0;
    if (productionQty <= 0) {
      Swal.fire({ title: "Enter a production quantity first", icon: "warning" });
      return;
    }

    for (const component of bomEntry.items) {
      const neededQty = bigMul(component.quantity, productionQty);
      let rate = 0;
      try {
        const res = await getCurrentStockApi({ itemId: component.item_id, warehouseId: 0 });
        const avgRate = parseFloat(res.data[0]?.average_rate || 0);
        const conversion = parseFloat(res.data[0]?.conversion || 1);
        rate = bigMul(avgRate, conversion);
      } catch (err) {
        // fall back to 0; user can still edit the rate on the raw row afterward
      }

      dispatch(
        applyRecipeRow({
          item_id: component.item_id,
          product: component.item_name,
          qty: neededQty,
          rate,
          per_unit_id: component.per_unit_id,
          warehouse_id: 0,
        })
      );
    }

    dispatch(calculateTotals());
  };

  const handleDeleteRow = (rowKey) => {
    if (type === "raw") {
      dispatch(deleteRawRow(rowKey));
    } else {
      dispatch(deleteProductionRow(rowKey));
    }
    dispatch(calculateTotals());
  };

  const columns = [
    {
      title: "Item",
      dataIndex: "product",
      width: 300,
      render: (_, record) => (
        <SearchListAutoComplete
          entityType="item"
          placeholder="Item"
          showAddNew={true}
          style={{ width: "100%" }}
          optionsList={items
            .filter((i) => i.is_service !== 'yes')
            .map((i) => ({
              sale_id: i.item_id,
              display_text: i.item_name,
              raw: i,
            }))
          }
          value={
            record.item_id
              ? { value: record.product, label: record.product }
              : undefined
          }
          onSelect={(obj) => {
            // 🔴 CASE 1: CLEAR
            if (!obj) {
              const clearUpdates = {
                item_id: null,
                product: "",
                uom: "",
                rate: 0,
                amount: 0,
                tax_per: 0,
                item_code: "",
                unit_id: 0,
                unit_name: "",
                unit_symbol: "",
                per_unit_id: 0,
                per_unit_symbol: "",
                per_conversion: 1,
                warehouse_id: 0,
                warehouse_name: "",
                base_unit_name: "",
                conversion: 1,
                units: [],
                qty: 0,
                current_stock: null
              };

              if (type === "raw") {
                dispatch(updateRawRow({ rowKey: record.key, updates: clearUpdates }));
              } else {
                dispatch(updateProductionRow({ rowKey: record.key, updates: clearUpdates }));
              }

              dispatch(calculateTotals());
              return;
            }
            if (obj === "add_new") {
              setProductDrawer({
                open: true,
                rowKey: record.key, // ✅ record is in scope here
              });
              return;
            }

            handleItemSelect(record.key, obj.raw);
          }}


        // onSelect={(obj) => {

        //   const it =
        //     items.find((i) => i.item_id === obj?.sale_id) || obj?.raw;

        //   const updates = {
        //     item_id: it?.item_id || null,
        //     product: it?.item_name || "",
        //     rate: it?.sale_rate ?? it?.item_rate ?? 0,
        //     uom: it?.unit_symbol || "",
        //     amount: (record.qty || 0) * (it?.sale_rate ?? it?.item_rate ?? 0),
        //     tax_per: it?.tax_per ?? 0,
        //     item_code: it?.item_code ?? "",
        //     item_percentage: type === "raw" ? 0 : productionCart.length === 0 ? 100 : 0,            
        //     unit_id: it?.unit_id || 0,
        //     unit_name: it?.unit_name || "",
        //     unit_symbol: it?.unit_symbol || "",
        //     per_unit_id: it?.unit_id || 0,
        //     per_unit_symbol: it?.unit_symbol || "",
        //     per_conversion: it?.conversion || 1,
        //     warehouse_id: it?.warehouse_id || 0,
        //     warehouse_name: it?.warehouse_name || "",
        //     base_unit_name: it?.base_unit_name || "",
        //     conversion: it?.conversion || 1,
        //     units: it?.units || [],
        //   };

        //   if (type === "raw") {
        //     dispatch(updateRawRow({ rowKey: record.key, updates }));
        //   } else {
        //     dispatch(updateProductionRow({ rowKey: record.key, updates }));
        //   }

        //   dispatch(calculateTotals());
        //   handleItemSelect(record.key, obj.raw);
        // }}
        />

      ),
    },

    {
      title: "Qty",
      dataIndex: "qty",

      width: 200,
      render: (_, record) => (
        <InputNumberField
          min={0}
          precision={1}
          step={0.5}
          value={record.qty}
          placeholder="0"
          onClick={() => {
            if (record.qty !== null) {
              handleQtyChange(record.key, null);
            }
          }}
          onChange={(val) => handleQtyChange(record.key, val)}
          style={{ width: "100%" }}
          addonAfter={record.unit_symbol || record.uom ? `(${record.unit_symbol || record.uom})` : ""}
          data-testid={`qty-input-${record.key}`}
          onKeyDown={(e) => {
            if (e.key === "." || e.key === "e" || e.key === "E") {
              e.preventDefault();
            }
          }}
        />
      ),
    },
    {
  title: "Qty",
  dataIndex: "qty",
  width: 200,
  render: (_, record) => (
    <InputNumberField
      min={0}
      precision={1}
      step={0.5}
      value={record.qty}
      placeholder="0"
      onClick={() => {
        if (record.qty !== null) {
          handleQtyChange(record.key, null);
        }
      }}
      onChange={(val) => handleQtyChange(record.key, val)}
      style={{
        width: "100%",
        minWidth: 120,
      }}
      addonAfter={
        record.unit_symbol || record.uom
          ? `(${record.unit_symbol || record.uom})`
          : ""
      }
      data-testid={`qty-input-${record.key}`}
      onKeyDown={(e) => {
        if (e.key === "." || e.key === "e" || e.key === "E") {
          e.preventDefault();
        }
      }}
    />
  ),
},
    // {
    //   // title: `Rate(${symbol})`,
    //   title: "Rate",
    //   dataIndex: "rate",
    //   render: (value) => {
    //     const num = parseFloat(value || 0);
    //     return num.toFixed(2);
    //   },
    // },
    {
      title: "Rate",
      dataIndex: "rate",
      render: (_, record) => {
        if (type === "raw" && record.manual_rate) {
          return (
            <InputNumberField
              min={1}
              max={100000}
              value={record.rate}
              onChange={(val) => {
                if (val <= 0) return;

                dispatch(updateRawRow({
                  rowKey: record.key,
                  updates: {
                    rate: val,
                    amount: bigMul(record.qty || 0, val),
                  },
                }));
                dispatch(calculateTotals());
              }}
              style={{ width: "150px" }}
            />
          );
        }

        return Number(record.rate || 0).toFixed(2);
      },
    },
    {
      // title: `Amount(${symbol})`,
      title: "Amount",
      dataIndex: "amount",
      render: (value) => {
        const num = parseFloat(value || 0);
        return num.toFixed(2);
      },
    },
    // {
    //   title: "UOM",
    //   dataIndex: "uom",
    // },
    {
      title: "Action",
      render: (_, record) => (
        <>
          {type === "production" && bomCache[record.item_id]?.bom && (
            <Button
              size="small"
              style={{ marginRight: 8 }}
              onClick={() => handleApplyRecipe(record)}
            >
              Apply Recipe
            </Button>
          )}
          <Button icon={<DeleteOutlined />} danger onClick={() => handleDeleteRow(record.key)}
            disabled={type === "raw" ? rawCart.length === 1 : productionCart.length === 1} />
        </>
      ),
    },
  ];

  return (
    <Card title={type === "raw" ? "Raw Materials" : "Production Items"} style={{ marginBottom: 20 }}>
      <Table
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        rowKey="key"
        scroll={{ x: "min-content" }}
      />
      <Button
        type="dashed"
        onClick={() =>
          type === "raw" ? dispatch(addRawRow()) : dispatch(addProductionRow())
        }
        icon={<PlusOutlined />}
        style={{ marginTop: 10 }}
      >
        Add Row
      </Button>

      {type === "production" && (
        <Card title="Manufacturing Costs" style={{ marginTop: 20 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6} lg={6} xl={6}>
              <label>Labour Cost (Inclusive) ({currency})</label>
              <InputNumber
                value={footerValues.labourCost}
                onChange={(val) => {
                  dispatch(setFooterValues({ labourCost: val || 0 }));
                  dispatch(calculateTotals());
                }}
                precision={2}
                min={0}
                style={{ width: "100%" }}
                onWheel={(e) => e.target.blur()}
              />
            </Col>
            <Col xs={24} sm={12} md={6} lg={6} xl={6}>
              <label>Other Cost (Inclusive) ({currency})</label>
              <InputNumber
                value={footerValues.otherCost}
                onChange={(val) => {
                  dispatch(setFooterValues({ otherCost: val || 0 }));
                  dispatch(calculateTotals());
                }}
                precision={2}
                min={0}
                style={{ width: "100%" }}
                onWheel={(e) => e.target.blur()}
              />
            </Col>
            <Col xs={24} sm={12} md={6} lg={6} xl={6}>
              <label>Total Cost ({currency})</label>
              <Input
                value={Number(footerValues.totalCost || 0).toFixed(2)}
                precision={2}
                readOnly
                style={{
                  backgroundColor: "#f5f5f5",
                  fontWeight: "bold",
                  width: "100%",
                }}
              />
            </Col>

            {/* <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item
                label={`Other Cost (Inclusive) (${currency})`}
                labelCol={{ span: 24 }}
                style={{ marginBottom: 0 }}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  value={footerValues.otherCost}
                  onChange={(val) => {
                    dispatch(setFooterValues({ otherCost: val || 0 }));
                    dispatch(calculateTotals());
                  }}
                />
              </Form.Item>
            </Col> */}

            {/* <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item
                label={`Total Cost (${currency})`}
                labelCol={{ span: 24 }}
                style={{ marginBottom: 0 }}
              >
                <Input
                  readOnly
                  value={footerValues.totalCost}
                  style={{
                    backgroundColor: "#f5f5f5",
                    fontWeight: "bold",
                    width: "100%",
                  }}
                />
              </Form.Item>
            </Col> */}

            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item
                label="Description"
                labelCol={{ span: 24 }}
                style={{ marginBottom: 0 }}
              >
                <Input.TextArea
                  autoSize={{ minRows: 1, maxRows: 3 }}
                  value={footerValues.description}
                  onChange={(e) =>
                    dispatch(setFooterValues({ description: e.target.value }))
                  }
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>
      )}

      <ProductQuickDrawer
        open={productDrawer.open}
        onClose={() => setProductDrawer({ open: false, rowKey: null })}
        onCreated={(product) => {
          if (!product || !productDrawer.rowKey) return;

          const updates = {
            item_id: product.item_id,
            product: product.item_name,
            rate: Number(product.sale_rate || 0),
            tax_per: product.tax_per ?? 0,
            item_code: product.item_code ?? "",
            unit_id: product.unit_id || 0,
            unit_name: product.unit_name || "",
            unit_symbol: product.unit_symbol || "",
            per_unit_id: product.unit_id || 0,
            per_unit_symbol: product.unit_symbol || "",
            per_conversion: product.conversion || 1,
            warehouse_id: product.warehouse_id || 0,
            warehouse_name: product.warehouse_name || "",
            base_unit_name: product.base_unit_name || "",
            conversion: product.conversion || 1,
            units: product.units || [],
            qty: 0,
            amount: 0,
          };

          if (type === "raw") {
            dispatch(updateRawRow({ rowKey: productDrawer.rowKey, updates }));
          } else {
            dispatch(updateProductionRow({ rowKey: productDrawer.rowKey, updates }));
          }

          dispatch(calculateTotals());

          // Refresh item list so dropdown includes new item
          dispatch(fetchItems());

          // Close drawer
          setProductDrawer({ open: false, rowKey: null });
        }}
      />


    </Card>
  );
};

export default ManufacturingTable;
